import { Box, type BoxProps } from '@chakra-ui/react';
import { sanitizeDom } from '@/lib/purify';
import { KText } from '@/ui-library/components/text';

type SanitizedProps<T> = Omit<T, 'children' | 'dangerouslySetInnerHTML'> & {
  html: string | null | undefined;
};

export const SanitizedText = ({
  html,
  ...props
}: SanitizedProps<React.ComponentProps<typeof KText>>) => {
  return (
    <KText
      dangerouslySetInnerHTML={{ __html: sanitizeDom(html) }}
      children={undefined}
      {...props}
    />
  );
};

export const SanitizedBox = ({ html, ...props }: SanitizedProps<BoxProps>) => {
  return (
    <Box dangerouslySetInnerHTML={{ __html: sanitizeDom(html) }} {...props} />
  );
};

// Restores browser-default styling for HTML rendered inside Chakra's CSSReset
// (paragraph margins, list bullets/numbers, bold strong, heading sizes, etc.).
// Use for API-supplied rich-text bodies (template previews, email previews,
// notes coming back as HTML).
const richHtmlSx = {
  'p, ul, ol, blockquote': { my: '0.75em' },
  'p:empty': { my: '0.5em' },
  'ul, ol': { pl: '1.5em' },
  ul: { listStyleType: 'disc' },
  ol: { listStyleType: 'decimal' },
  'li > p': { my: 0 },
  li: { my: '0.25em' },
  strong: { fontWeight: 'bold' },
  em: { fontStyle: 'italic' },
  h1: { fontSize: '1.5em', fontWeight: 'bold', my: '0.67em' },
  h2: { fontSize: '1.25em', fontWeight: 'bold', my: '0.75em' },
  h3: { fontSize: '1.1em', fontWeight: 'bold', my: '0.83em' },
  a: { color: 'primary-600', textDecoration: 'underline' },
  blockquote: {
    borderLeft: '3px solid',
    borderColor: 'secondary-200',
    pl: '12px',
    color: 'secondary-600',
  },
  'pre, code': {
    fontFamily: 'mono',
    fontSize: '0.9em',
    bg: 'primary-50',
    borderRadius: '4px',
    px: '4px',
  },
  img: { maxW: '100%' },
};

export const RichHtml = ({ html, sx, ...props }: SanitizedProps<BoxProps>) => {
  return (
    <SanitizedBox
      html={html}
      sx={sx ? { ...richHtmlSx, ...sx } : richHtmlSx}
      {...props}
    />
  );
};
